import  { useState, useEffect,memo } from "react";
import { showToast } from "../../Buttons/Toaster";
import "../../../components/CSSCustom/toaster.css";
import Form from "react-bootstrap/Form";
import "../../CSSCustom/adduser.css";
import CustomTooltip from "../../Buttons/CustomTooltip";

function AddUser({ show, setShow, user, onSave }) {
  // State for all form fields
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    firstname: "",
    lastname: "",
    role_name: "",
  });

  // State for error messages
  const [errors, setErrors] = useState({
    username: "",
    email: "",
    password: "",
    firstname: "",
    lastname: "",
    role_name: "",
  });

  const [touched, setTouched] = useState({
    username: false,
    email: false,
    password: false,
    firstname: false,
    lastname: false,
    role_name: false,
  });

  const [duplicateError, setDuplicateError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const roleOptions = [
    { id: "user", name: "user" },
    { id: "admin", name: "admin" },
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Determine labels based on API endpoint
  const apiEndpoint = `${process.env.REACT_APP_API_URL_CODE_CONVERSION}/register`;

  const isMigrationEndpoint = apiEndpoint;
  const usernameLabel = isMigrationEndpoint ? "User Name" : "Migration Name";
  const emailLabel = isMigrationEndpoint ? "Email Id" : "Migration Email";
  const firstnameLabel = isMigrationEndpoint
    ? "First Name"
    : "Migration First Name";
  const lastnameLabel = isMigrationEndpoint
    ? "Last Name"
    : "Migration Last Name";
  const passwordLabel = isMigrationEndpoint ? "Password" : "Migration Password";
  const roleLabel = isMigrationEndpoint ? "Select Role" : "Migration Role";

  // Field validation functions
  const validateUsername = (username) => {
    if (!username.trim()) return "Username is required";
    if (!/^[a-zA-Z0-9_]+$/.test(username))
      return "Only letters, numbers and underscores allowed";
    if (username.length < 3 || username.length > 50)
      return "Username must be between 3-50 characters";
    return "";
  };

  const validateEmail = (email) => {
    if (!email.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return "Invalid email format";
    if (email.length > 100) return "Email must be less than 100 characters";
    return "";
  };

  const validatePassword = (password) => {
    if (!password && !user) return "Password is required";
    if (password && (password.length < 8 || password.length > 50))
      return "Password must be 8-50 characters";
    return "";
  };

  const validateFirstName = (name) => {
    if (!name.trim()) return "First name is required";
    if (!/^[a-zA-Z0-9 ]+$/.test(name))
      return "Only letters, numbers and spaces allowed";
    if (name.length < 3 || name.length > 100)
      return "First name must be between 3-100 characters";
    return "";
  };

  const validateLastName = (name) => {
    if (!name.trim()) return "Last name is required";
    if (!/^[a-zA-Z0-9 ]+$/.test(name))
      return "Only letters, numbers and spaces allowed";
    return "";
  };

  const validateRole = (role) => {
    if (!role) return "Role is required";
    return "";
  };

  // Clear form completely
  const clearForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      firstname: "",
      lastname: "",
      role_name: "",
    });
    setErrors({
      username: "",
      email: "",
      password: "",
      firstname: "",
      lastname: "",
      role_name: "",
    });
    setTouched({
      username: false,
      email: false,
      password: false,
      firstname: false,
      lastname: false,
      role_name: false,
    });
    setDuplicateError(null);
  };

  // Handle close/cancel action
  const handleClose = () => {
    clearForm();
    setShow(false);
  };

  // Initialize form when user data is provided (for editing)
  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || "",
        email: user.email || "",
        password: "", // Password typically not retrieved for editing
        firstname: user.firstname || "",
        lastname: user.lastname || "",
        role_name: user.role_name || "",
      });
    } else {
      clearForm();
    }
  }, [user]);

  // Validate field on change and blur
  const validateField = (name, value) => {
    switch (name) {
      case "username":
        return validateUsername(value);
      case "email":
        return validateEmail(value);
      case "password":
        return validatePassword(value);
      case "firstname":
        return validateFirstName(value);
      case "lastname":
        return validateLastName(value);
      case "role_name":
        return validateRole(value);
      default:
        return "";
    }
  };

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate immediately if the field has been touched
    if (touched[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }

    if (duplicateError) setDuplicateError(null);
  };

  // Handle blur events
  const handleBlur = (e) => {
    const { name, value } = e.target;

    // Mark field as touched
    setTouched((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Validate the field
    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  // Validate all fields before submission
  const validateForm = () => {
    const newErrors = {
      username: validateUsername(formData.username),
      email: validateEmail(formData.email),
      password: validatePassword(formData.password),
      firstname: validateFirstName(formData.firstname),
      lastname: validateLastName(formData.lastname),
      role_name: validateRole(formData.role_name),
    };

    setErrors(newErrors);

    // Mark all fields as touched to show errors
    setTouched({
      username: true,
      email: true,
      password: true,
      firstname: true,
      lastname: true,
      role_name: true,
    });

    return !Object.values(newErrors).some((error) => error);
  };

  // Handle form submission
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setDuplicateError(null);

    try {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken) {
        throw new Error("Authentication token missing");
      }

      // Format names (capitalize first letters)
      const formattedData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        firstname: formData.firstname
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        lastname: formData.lastname
          .toLowerCase()
          .split(" ")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
        role_name: formData.role_name,
      };

      // Create FormData for the request
      const formDataPayload = new FormData();
      formDataPayload.append("username", formattedData.username);
      formDataPayload.append("email", formattedData.email);
      formDataPayload.append("password", formattedData.password);
      formDataPayload.append("firstname", formattedData.firstname);
      formDataPayload.append("lastname", formattedData.lastname);
      formDataPayload.append("role_name", formattedData.role_name);

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formDataPayload,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 409) {
          throw new Error("Username or email already exists");
        }
        throw new Error(errorData.detail || "Failed to register user");
      }

      const result = await response.json();
      showToast("User registered successfully!", "success");

      // Clear form and close
      handleClose();

      // Call callback if provided
      if (onSave) {
        onSave(result);
      }
    } catch (error) {
      console.error("Registration failed:", error);
      if (error.message.includes("already exists")) {
        setDuplicateError(error.message);
        showToast(error.message, "error");
      } else {
        showToast(error.message || "Failed to register user", "error");
      }
    } finally {
      setIsSubmitting(false); // This ensures button is re-enabled in all cases
    }
  };

  return (
    <div
      className={`right-bar3 p-3 ${show ? "d-block" : "d-none"}`}
      style={{ width: "auto" }}
    >
      <div>
        <div className="d-flex align-items-center justify-content-between">
          <h6 className="font-size-13 mb-0 fw-medium text-dark">
            {user ? "Edit User" : "Register New User"}
          </h6>
          <CustomTooltip
            content="Close"
            placement="left"
            fallbackPlacements={["bottom"]}
          >
            <i
              onClick={handleClose}
              className="mdi mdi-close close-trace cursor-pointer right-bar-toggle3 iconColor"
            />
          </CustomTooltip>
        </div>
        <div className="trace-label  mt-1 pe-3 d-flex align-items-center mb-3 w-fit-content">
          <img
            src="/assets/images/trace_label.png"
            className="w-14px me-2"
            alt="Job icon"
          />
          <h6 className="font-size-10 mb-0 text-dark">
            {" "}
            Assign a role when registering a new user
          </h6>
        </div>
        <div className="mt-3">
          {/* Scrollable section starts here - FIXED VERSION */}
          <div
            style={{
              maxHeight: "calc(100vh - 180px)",
              overflowY: "auto",
              paddingLeft: "12px",
              paddingRight: "12px",
              marginLeft: "-14px",
              marginRight: "-14px",
            }}
          >
            <div>
              <h6 className="fw-sbold font-size-12">
                {roleLabel} <span style={{ color: "red" }}>*</span>
              </h6>
              <Form.Select
                name="role_name"
                value={formData.role_name}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`font-size-12 ${
                  formData.role_name === "" ? "select-placeholder" : ""
                } ${errors.role_name && touched.role_name ? "is-invalid" : ""}`}
              >
                <option value="">Select a Role</option>
                {roleOptions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </Form.Select>
              {errors.role_name && touched.role_name && (
                <div className="text-danger font-size-10">
                  {errors.role_name}
                </div>
              )}

              <h6 className="fw-sbold font-size-12 mt-3 ">
                {firstnameLabel} <span style={{ color: "red" }}>*</span>
              </h6>
              <input
                type="text"
                className={`form-control highlighted-input font-size-12 doc-search mt-2 colorfor-placeholder styled-input ${
                  errors.firstname && touched.firstname ? "is-invalid" : ""
                }`}
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder={`Enter ${firstnameLabel.toLowerCase()}`}
              />
              {errors.firstname && touched.firstname && (
                <div className="text-danger font-size-10">
                  {errors.firstname}
                </div>
              )}

              <h6 className="fw-sbold font-size-12 mt-3">
                {lastnameLabel} <span style={{ color: "red" }}>*</span>
              </h6>
              <input
                type="text"
                className={`form-control highlighted-input font-size-12 doc-search mt-2 colorfor-placeholder styled-input ${
                  errors.lastname && touched.lastname ? "is-invalid" : ""
                }`}
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder={`Enter ${lastnameLabel.toLowerCase()}`}
              />
              {errors.lastname && touched.lastname && (
                <div className="text-danger font-size-10">
                  {errors.lastname}
                </div>
              )}

              <h6 className="fw-sbold font-size-12 mt-3">
                {usernameLabel} <span style={{ color: "red" }}>*</span>
              </h6>
              <input
                type="text"
                className={`form-control highlighted-input font-size-12 doc-search mt-2 colorfor-placeholder styled-input ${
                  (errors.username && touched.username) || duplicateError
                    ? "is-invalid"
                    : ""
                }`}
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder={`Enter ${usernameLabel.toLowerCase()}`}
              />
              {errors.username && touched.username && (
                <div className="text-danger font-size-10">
                  {errors.username}
                </div>
              )}
              {duplicateError && !errors.username && (
                <div className="text-danger font-size-10">{duplicateError}</div>
              )}

              {!user && (
                <>
                  <h6 className="fw-sbold font-size-12 mt-3">
                    {passwordLabel} <span style={{ color: "red" }}>*</span>
                  </h6>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      className={`form-control highlighted-input font-size-12 doc-search colorfor-placeholder styled-input ${
                        errors.password && touched.password ? "is-invalid" : ""
                      }`}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder={`Enter ${passwordLabel.toLowerCase()}`}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <i
                        className={`mdi ${
                          showPassword ? "mdi-eye-off" : "mdi-eye"
                        }`}
                      ></i>
                    </button>
                  </div>
                  {errors.password && touched.password && (
                    <div className="text-danger font-size-10">
                      {errors.password}
                    </div>
                  )}
                </>
              )}

              <h6 className="fw-sbold font-size-12 mt-3">
                {emailLabel} <span style={{ color: "red" }}>*</span>
              </h6>
              <input
                type="email"
                className={`form-control highlighted-input font-size-12 doc-search mt-2 colorfor-placeholder styled-input ${
                  (errors.email && touched.email) || duplicateError
                    ? "is-invalid"
                    : ""
                }`}
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                placeholder={`Enter ${emailLabel.toLowerCase()}`}
              />
              {errors.email && touched.email && (
                <div className="text-danger font-size-10">{errors.email}</div>
              )}
              {duplicateError && !errors.email && (
                <div className="text-danger font-size-10">{duplicateError}</div>
              )}

              {/* Add some bottom padding to ensure last field is fully visible */}
              <div style={{ height: "20px" }}></div>
            </div>
          </div>
          {/* Scrollable section ends here */}

          <div className="button-container mt-3">
            <button
              type="button"
              className="btn btn-closebtn custom-button"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Close
            </button>
            <button
              type="button"
              className="btn btn-primary custom-button"
              onClick={handleSave}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(AddUser);
